import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeIcon from '@mui/icons-material/QrCode';

const LinksPaper = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
});

const SectionTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.8rem',
  color: '#1a1a1a',
  marginBottom: '1.5rem',
});

const GeneratorBox = styled(Box)({
  backgroundColor: '#fff',
  padding: '2rem',
  border: '1px solid #eee',
  marginBottom: '2rem',
});

const StyledButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: 'white',
  padding: '0.8rem 2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const Links = () => {
  const [productUrl, setProductUrl] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '' });
  
  // Mock data - replace with real data from backend
  const [links] = useState([
    {
      id: 1,
      originalUrl: 'https://customconnectjewelry.com/products/diamond-solitaire-ring',
      affiliateUrl: 'https://customconnectjewelry.com/ref/jane-smith/diamond-ring',
      clicks: 245,
      sales: 12,
      earnings: 900,
      created: '2025-03-15',
    },
    {
      id: 2,
      originalUrl: 'https://customconnectjewelry.com/products/pearl-necklace',
      affiliateUrl: 'https://customconnectjewelry.com/ref/jane-smith/pearl-necklace',
      clicks: 189,
      sales: 8,
      earnings: 600,
      created: '2025-03-16',
    },
  ]);

  const handleGenerateLink = () => {
    if (!productUrl) return;
    
    // TODO: Generate affiliate link through backend
    console.log('Generating link for:', productUrl);
    setProductUrl('');
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setNotification({
      open: true,
      message: 'Link copied to clipboard!'
    });
  };

  const handleShare = (link) => {
    if (navigator.share) {
      navigator.share({
        title: 'Check out this beautiful piece',
        url: link,
      });
    } else {
      handleCopyLink(link);
    }
  };

  const handleGenerateQR = (link) => {
    // TODO: Generate and download QR code
    console.log('Generating QR code for:', link);
  };

  return (
    <LinksPaper elevation={0}>
      <SectionTitle>Affiliate Links</SectionTitle>

      <GeneratorBox>
        <Typography variant="h6" gutterBottom>
          Generate New Link
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Product URL"
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            placeholder="Paste product URL here"
            size="small"
          />
          <StyledButton
            variant="contained"
            onClick={handleGenerateLink}
            disabled={!productUrl}
          >
            Generate
          </StyledButton>
        </Box>
      </GeneratorBox>

      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Original URL</TableCell>
              <TableCell>Affiliate Link</TableCell>
              <TableCell align="right">Clicks</TableCell>
              <TableCell align="right">Sales</TableCell>
              <TableCell align="right">Earnings</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.id}>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {link.originalUrl}
                </TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {link.affiliateUrl}
                </TableCell>
                <TableCell align="right">{link.clicks}</TableCell>
                <TableCell align="right">{link.sales}</TableCell>
                <TableCell align="right">${link.earnings}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyLink(link.affiliateUrl)}
                    title="Copy Link"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleShare(link.affiliateUrl)}
                    title="Share Link"
                  >
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleGenerateQR(link.affiliateUrl)}
                    title="Generate QR Code"
                  >
                    <QrCodeIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {links.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No affiliate links generated yet
          </Typography>
          <Typography color="textSecondary">
            Generate your first link by pasting a product URL above
          </Typography>
        </Box>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
      />
    </LinksPaper>
  );
};

export default Links;
